import React from 'react';
import PropTypes from 'prop-types';
import zxcvbn from 'zxcvbn';
import {FormattedMessage} from 'react-intl';
import {FormField} from 'components/inputs/FormField';
import {InputLabel} from 'components/inputs/InputLabel';
import {TextInput} from 'components/inputs/Input';
import {Button} from 'components/buttons/Button';
import messages from './messages';
import {InputHelpText} from 'components/inputs/InputHelpText';
import AcceptConsentCheckbox from 'services/consent/components/AcceptConsentCheckbox';
import {CONSENT_TYPES} from 'services/consent/consentTypes';
import {FormWarning} from 'components/inputs/FormWarning';
import {Header} from 'components/primitives/header';
import {DisplaySmall} from 'components/primitives/display';

const getProgressClass = (value) => {
    if (value < 1) {
        return 'is-danger';
    }
    else if (value < 2) {
        return 'is-warning';
    }
    return 'is-success';
};


class CreateAccountForm extends React.Component {
    static propTypes = {
        email: PropTypes.string.isRequired,
        save: PropTypes.func.isRequired,
        loading: PropTypes.bool.isRequired,
        firstName: PropTypes.string,
        lastName: PropTypes.string
    };

    constructor(props) {
        super(props);

        this.requiredStrength = 2;

        this.state = {
            triedToSubmit: false,
            notificationForceClosed: false,
            strength: -1,
            formData: {
                firstName: props.firstName || '',
                lastName: props.lastName || '',
                password: ''
            },
            passwordFeedback: '',
            progressClass: '',
            consents: {
                MARKETING_POLICY: null,
                TERMS_OF_SERVICE: null
            }
        };
    }

    isPasswordStrongEnough = () => {
        return this.state.strength >= this.requiredStrength;
    };

    submitForm = () => {
        const {password, firstName, lastName} = this.state.formData;
        const {consents} = this.state;

        if (this.hasErrors()) {
            return;
        }
        const termsOfServiceVersion = consents[CONSENT_TYPES.TERMS_OF_SERVICE].revision.toString();
        const marketingPolicyVersion = consents[CONSENT_TYPES.MARKETING_POLICY].revision.toString();

        this.props.save({
            password,
            firstName,
            lastName,
            termsOfServiceVersion: termsOfServiceVersion,
            marketingPolicyVersion: marketingPolicyVersion,
            isMarketingPolicyApproved: this.isConsentAccepted(CONSENT_TYPES.MARKETING_POLICY)
        });
    };

    handleSubmit = (e) => {
        e.preventDefault();
        this.setState({triedToSubmit: true, notificationForceClosed: false}, this.submitForm);
    };

    hasErrors = () => {
        return !this.areAllRequiredConsentsAccepted()
            || !this.isPasswordStrongEnough()
            || this.hasFieldError('firstName')
            || this.hasFieldError('lastName');
    };

    areAllRequiredConsentsAccepted = () => {
        return this.isConsentAccepted(CONSENT_TYPES.TERMS_OF_SERVICE);
    };

    isConsentAccepted = (consentType) => {
        const {consents} = this.state;
        return consents[consentType] && consents[consentType].accepted;
    };


    shouldShowAgreementWarning = () => {
        return this.state.triedToSubmit && !this.areAllRequiredConsentsAccepted();
    };

    handleChange = (e) => {
        const {name, value} = e.target;
        this.setState(prevState => ({
            formData: Object.assign({}, prevState.formData, {[name]: value})
        }));
    };

    handlePasswordChange = (e) => {
        const password = e.target.value;
        this.updatePasswordState(password);
    };

    updatePasswordState = (password) => {
        const z = zxcvbn(password);
        const passwordFeedback = z.feedback.suggestions ? z.feedback.suggestions[0] : '';
        const progressClass = getProgressClass(z.score);

        this.setState(prevState => ({
            strength: z.score,
            passwordFeedback,
            progressClass,
            formData: Object.assign({}, prevState.formData, {password})
        }));
    };

    onConsentApprovalChanged = (consent) => {
        this.setState(prevState => ({
            consents: Object.assign({}, prevState.consents, {[consent.type]: consent})
        }));
    };

    showPasswordError = () => {
        return this.state.triedToSubmit && !this.isPasswordStrongEnough();
    };

    hasFieldError = (field) => {
        const {formData} = this.state;
        return !formData[field];
    };

    shouldShowFieldError = (field) => {
        const {formData} = this.state;
        return !formData[field] && this.state.triedToSubmit;
    };

    shouldShowNotification = () => {
        return !this.state.notificationForceClosed && this.state.triedToSubmit && this.hasErrors();
    };

    forceCloseNotification = () => {
        this.setState({notificationForceClosed: true});
    };

    render() {
        const progressStyle = {
            height: '6px',
            border: '1px solid 1px solid rgb(202, 202, 202)'
        };

        return (
            <form className='form' onSubmit={this.handleSubmit}>
                <FormWarning visible={this.shouldShowNotification()} onClose={this.forceCloseNotification}>
                    <FormattedMessage {...messages.warning}/>
                </FormWarning>
                <Header>
                    <DisplaySmall>
                        <FormattedMessage {...messages.title}/>
                    </DisplaySmall>
                </Header>
                <FormField>
                    <InputLabel>
                        <FormattedMessage {...messages.email}/>
                    </InputLabel>

                    <TextInput
                        type="text"
                        fullWidth={true}
                        defaultValue={this.props.email}
                        disabled/>
                </FormField>
                <FormField>
                    <InputLabel>
                        <FormattedMessage {...messages.firstName}/>
                    </InputLabel>

                    <FormattedMessage {...messages.enterMandatory}>
                        {(enterMandatory) => (
                            <TextInput
                                type="text"
                                name='firstName'
                                hasError={this.shouldShowFieldError('firstName')}
                                placeholder={enterMandatory}
                                fullWidth={true}
                                onChange={this.handleChange}
                                value={this.state.formData.firstName}/>
                        )}
                    </FormattedMessage>
                    {this.shouldShowFieldError('firstName') ? (
                        <InputHelpText>
                            <FormattedMessage {...messages.firstNameError} />
                        </InputHelpText>
                    ) : null}
                </FormField>
                <FormField>
                    <InputLabel>
                        <FormattedMessage {...messages.lastName}/>
                    </InputLabel>

                    <FormattedMessage {...messages.enterMandatory}>
                        {(enterMandatory) => (
                            <TextInput
                                type="text"
                                name='lastName'
                                hasError={this.shouldShowFieldError('lastName')}
                                placeholder={enterMandatory}
                                fullWidth={true}
                                onChange={this.handleChange}
                                value={this.state.formData.lastName}/>
                        )}
                    </FormattedMessage>
                    {this.shouldShowFieldError('lastName') ? (
                        <InputHelpText>
                            <FormattedMessage {...messages.lastNameError} />
                        </InputHelpText>
                    ) : null}
                </FormField>
                <FormField>
                    <InputLabel>
                        <FormattedMessage {...messages.password}/>
                    </InputLabel>

                    <FormattedMessage {...messages.selectPassword}>
                        {selectPassword => (
                            <TextInput
                                hasError={this.showPasswordError()}
                                type="password"
                                name="password"
                                onChange={this.handlePasswordChange}
                                autoComplete="off"
                                placeholder={selectPassword}
                                fullWidth={true}
                                value={this.state.formData.password}
                            />
                        )}
                    </FormattedMessage>
                    <div style={{margin: '8px 0'}}>
                        <progress
                            className={`progress ${this.state.progressClass}`} value={this.state.strength + 1}
                            max={4 + 1} style={progressStyle}/>
                    </div>
                    <p className='help'>
                        {this.state.passwordFeedback}
                    </p>
                </FormField>

                <FormField>
                    <AcceptConsentCheckbox
                        type='MARKETING_POLICY'
                        onChange={this.onConsentApprovalChanged}/>
                    <AcceptConsentCheckbox
                        type='TERMS_OF_SERVICE' linkToFull='/terms'
                        onChange={this.onConsentApprovalChanged}/>
                    {this.shouldShowAgreementWarning() ? (
                        <InputHelpText>
                            <FormattedMessage {...messages.termsNotAccepted}/>
                        </InputHelpText>
                    ) : null}
                </FormField>

                <FormField>
                    <Button
                        lookDisabled={this.state.triedToSubmit && this.hasErrors()}
                        type="submit"
                        loading={this.props.loading}
                        style={{minWidth: '140px'}}
                    >
                        <FormattedMessage {...messages.createAccount}/>
                    </Button>
                </FormField>

            </form>
        );
    }
}


export default CreateAccountForm;
